import { type SortField } from '__generated__/graphql'
import { createContext, type ReactNode } from 'react'
import { useUrlState } from '../hooks/useUrlState'
import {
    type CandidateFilterState,
    type SortState,
    DEFAULT_FILTERS,
    encodeFilters,
    decodeFilters,
    encodeSort,
    decodeSort,
} from '../hooks/clientPortalCodecs'

const URL_STATE_DEFAULTS = {
    rosterCandidate: '',
    page: 1,
    limit: 50,
    sort: '',
    filters: '',
}

export interface CandidateSqlSearchContextValue {
    activeSortFields: SortField[]
    setActiveSortFields: (sort: SortField[]) => void
    rosterCandidateId: string | undefined
    setRosterCandidateId: (id: string | undefined) => void
    page: number
    limit: number
    setPage: (page: number) => void
    setLimit: (limit: number) => void
    filters: CandidateFilterState
    setFilters: (filters: CandidateFilterState) => void
    clearFilters: () => void
}

export const CandidateSqlSearchContext =
    createContext<CandidateSqlSearchContextValue | null>(null)

interface CandidateSqlSearchProviderProps {
    children: ReactNode
}

export function CandidateSqlSearchProvider({
    children,
}: CandidateSqlSearchProviderProps) {
    const [urlState, setUrlState] = useUrlState(URL_STATE_DEFAULTS)

    const activeSortFields = decodeSort(urlState.sort) as SortField[]
    const setActiveSortFields = (sort: SortField[]) => {
        setUrlState({ sort: encodeSort(sort as SortState) })
    }

    const rosterCandidateId = urlState.rosterCandidate || undefined
    const page = urlState.page
    const limit = urlState.limit

    const setRosterCandidateId = (id: string | undefined) => {
        setUrlState({ rosterCandidate: id || '' })
    }

    const setPage = (newPage: number) => {
        setUrlState({ page: newPage })
    }

    const setLimit = (newLimit: number) => {
        setUrlState({ limit: newLimit, page: 1 })
    }

    const filters = decodeFilters(urlState.filters)
    const setFilters = (newFilters: CandidateFilterState) => {
        setUrlState({ filters: encodeFilters(newFilters), page: 1 })
    }
    const clearFilters = () => {
        const cleared = encodeFilters({
            ...DEFAULT_FILTERS,
            processStatusGroup: filters.processStatusGroup,
        })
        setUrlState({ filters: cleared, page: 1 })
    }

    const value: CandidateSqlSearchContextValue = {
        // page
        page,
        setPage,

        // limit
        limit,
        setLimit,

        // sort
        activeSortFields,
        setActiveSortFields,

        // filters
        filters,
        setFilters,
        clearFilters,

        // what candidate is open in the drawer
        rosterCandidateId,
        setRosterCandidateId,
    }

    return (
        <CandidateSqlSearchContext.Provider value={value}>
            {children}
        </CandidateSqlSearchContext.Provider>
    )
}
